import { Booking } from '@/types';

/**
 * Booking conflict detection for the Planner.
 *
 * Prevents overlapping bookings for the SAME resource on the SAME date. The
 * rules mirror the four booking shapes the app supports:
 *
 *  - Full Day  → occupies the whole day  [00:00, 24:00)
 *  - Morning   → half-day AM             [00:00, 12:00)
 *  - Afternoon → half-day PM             [12:00, 24:00)
 *  - Custom    → hourly, uses the actual start/end times
 *
 * Two bookings conflict when they share a resource, share a calendar date, and
 * their minute-of-day intervals overlap using the standard half-open interval
 * rule:  newStart < existingEnd && newEnd > existingStart.
 *
 * All date/time values are read directly from the stored ISO strings via regex
 * (never `new Date(iso)`) so the comparison is timezone-safe and consistent
 * with how bookings are persisted elsewhere in the app.
 */

const MINUTES_PER_DAY = 24 * 60;

interface DaySegment {
  /** YYYY-MM-DD */
  dateKey: string;
  /** Inclusive start, minutes from midnight. */
  startMin: number;
  /** Exclusive end, minutes from midnight. */
  endMin: number;
}

export interface BookingConflictResult {
  conflict: boolean;
  /** The existing booking the candidate overlaps with, if any. */
  conflictingBooking?: Booking;
}

const isoToDateKey = (iso: string): string => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Minutes from midnight extracted from the time portion of an ISO string. */
const isoToMinutes = (iso: string): number => {
  const m = iso.match(/T(\d{2}):(\d{2})/);
  if (!m) return 0;
  return Number(m[1]) * 60 + Number(m[2]);
};

const isoIsMidnight = (iso: string): boolean => {
  const m = iso.match(/T(\d{2}):(\d{2}):(\d{2})/);
  if (m) return m[1] === '00' && m[2] === '00' && m[3] === '00';
  // Date-only string → treat as midnight
  return !iso.includes('T');
};

const prevDayKey = (key: string): string => {
  const [y, mo, d] = key.split('-').map(Number);
  const dt = new Date(y, mo - 1, d);
  dt.setDate(dt.getDate() - 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const nextDayKey = (key: string): string => {
  const [y, mo, d] = key.split('-').map(Number);
  const dt = new Date(y, mo - 1, d);
  dt.setDate(dt.getDate() + 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const resolveHalfDayPeriod = (booking: Booking): 'am' | 'pm' | null => {
  if (booking.durationType === 'half-day-am') return 'am';
  if (booking.durationType === 'half-day-pm') return 'pm';
  if (booking.durationType === 'half-day') {
    if (booking.halfDayPeriod) return booking.halfDayPeriod;
    // Fall back to the stored start time when the period isn't explicit.
    return isoToMinutes(booking.startTime) < 720 ? 'am' : 'pm';
  }
  return null;
};

/**
 * Decompose a booking into the day-segments it occupies. Each segment is a
 * minute-of-day interval on a single calendar date.
 */
export function getBookingSegments(booking: Booking): DaySegment[] {
  const startKey = isoToDateKey(booking.startTime);

  // ── Hourly / custom bookings — use the real times ──────────────────────
  if (booking.durationType === 'hourly') {
    const endKey = isoToDateKey(booking.endTime);
    const startMin = isoToMinutes(booking.startTime);
    const endMin = isoToMinutes(booking.endTime);

    if (startKey === endKey) {
      if (endMin <= startMin) return [];
      return [{ dateKey: startKey, startMin, endMin }];
    }

    // Multi-day hourly booking: split across the spanned days.
    const segments: DaySegment[] = [{ dateKey: startKey, startMin, endMin: MINUTES_PER_DAY }];
    let cursor = nextDayKey(startKey);
    while (cursor < endKey) {
      segments.push({ dateKey: cursor, startMin: 0, endMin: MINUTES_PER_DAY });
      cursor = nextDayKey(cursor);
    }
    if (endMin > 0) segments.push({ dateKey: endKey, startMin: 0, endMin });
    return segments;
  }

  // ── Half-day (morning / afternoon) — always single day ─────────────────
  const period = resolveHalfDayPeriod(booking);
  if (period === 'am') return [{ dateKey: startKey, startMin: 0, endMin: 720 }];
  if (period === 'pm') return [{ dateKey: startKey, startMin: 720, endMin: MINUTES_PER_DAY }];

  // ── Full day (possibly multi-day) ──────────────────────────────────────
  let endKey = isoToDateKey(booking.endTime);
  // Bryntum stores exclusive end-dates at 00:00 of the NEXT day — walk back.
  if (isoIsMidnight(booking.endTime)) endKey = prevDayKey(endKey);
  if (endKey < startKey) endKey = startKey;

  const segments: DaySegment[] = [];
  let cursor = startKey;
  // Guard against pathological ranges.
  for (let i = 0; i < 3660 && cursor <= endKey; i++) {
    segments.push({ dateKey: cursor, startMin: 0, endMin: MINUTES_PER_DAY });
    cursor = nextDayKey(cursor);
  }
  return segments;
}

const getResourceIds = (booking: Booking): string[] => {
  if (booking.resourceIds && booking.resourceIds.length > 0) {
    return booking.resourceIds.map(String);
  }
  if (booking.resourceId != null) return [String(booking.resourceId)];
  return [];
};

/** Half-open interval overlap: aStart < bEnd && aEnd > bStart. */
const intervalsOverlap = (a: DaySegment, b: DaySegment): boolean =>
  a.dateKey === b.dateKey && a.startMin < b.endMin && a.endMin > b.startMin;

/**
 * Returns whether `candidate` conflicts with any booking in `existingBookings`
 * for the same resource on the same date, along with the first offender.
 *
 * Bookings that are cancelled, unallocated placeholders, or the candidate
 * itself (matched by id) are ignored.
 */
export function findBookingConflict(
  candidate: Pick<Booking,
    'startTime' | 'endTime' | 'durationType' | 'halfDayPeriod' | 'resourceId' | 'resourceIds'
  > & Partial<Booking>,
  existingBookings: Booking[],
): BookingConflictResult {
  const candidateResourceIds = getResourceIds(candidate as Booking).filter(id => id !== 'unallocated');
  if (candidateResourceIds.length === 0) return { conflict: false };

  const candidateSegments = getBookingSegments(candidate as Booking);
  if (candidateSegments.length === 0) return { conflict: false };

  const candidateId = candidate.id != null ? String(candidate.id) : undefined;
  const candidateResourceSet = new Set(candidateResourceIds);

  for (const existing of existingBookings) {
    if (candidateId != null && String(existing.id) === candidateId) continue;
    if (existing.status === 'cancelled') continue;

    const existingResourceIds = getResourceIds(existing).filter(id => id !== 'unallocated');
    if (existingResourceIds.length === 0) continue;
    if (!existingResourceIds.some(id => candidateResourceSet.has(id))) continue;

    const existingSegments = getBookingSegments(existing);
    for (const cs of candidateSegments) {
      for (const es of existingSegments) {
        if (intervalsOverlap(cs, es)) {
          return { conflict: true, conflictingBooking: existing };
        }
      }
    }
  }

  return { conflict: false };
}
